// import { ALT, ENV, IS_BROWSER } from '../baseConfig';
import { reportEvent } from './sentry';

let memoizedClientFingerprint: string | null = null;
let memoizedVisitorFingerprint: string | null = null;

// Process Ideally begins at "getFingerprint"
const getFingerprint = async (): Promise<string> => {
    if (memoizedClientFingerprint) {
        return memoizedClientFingerprint;
    }
    await scanFingerprint();
    if (memoizedClientFingerprint) {
        return memoizedClientFingerprint;
    }
    reportEvent('Could not get fingerprint', 'gerFingerprint', 'warning');
    return 'sus';
};
export default getFingerprint;

declare global {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Window {
        __fingerprint: any;
    }
}

// Collects and saves fingerprint
const scanFingerprint = async (): Promise<void> => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        const fingerprintJS = await import('@fingerprintjs/fingerprintjs');

        /* FPJS 3.0 --- Doesn't include WebGL. Use this when that's sorted.
        const fingerprint = await fingerprintJS.load();
        const fingerprintObject = await fingerprint.get();
        memoizedClientFingerprint = await encrypt({ ...fingerprintObject });
        */

        /* Start FPJS 2.0 */
        const fingerprintReadLegacy = new Promise(resolve => {
            fingerprintJS.get(components => {
                resolve(components);
            });
        });
        let fingerprintResult: any = await Promise.resolve(fingerprintReadLegacy);

        // Compress WebGL and Canvas
        try {
            fingerprintResult = fingerprintResult.map(item => {
                if (item.key === 'canvas' || item.key === 'webgl') {
                    if (typeof item.value === 'string') {
                        return { key: item.key, value: item.value };
                    }
                    const modifiedValue = item.value
                        .map(val => {
                            if (val.includes('data:image/png;base64,')) {
                                return compressCanvas(val);
                            }
                            return null;
                        })
                        .filter(x => x)[0];
                    return { key: item.key, value: modifiedValue };
                }
                return item;
            });
        } catch (err) {
            reportEvent('Issue with compressing fingerprint', 'Fingerprint.initialize()', 'warning', { err });
        }

        const values = fingerprintResult.map(component => component.value);
        const visitorFingerprint = fingerprintJS.x64hash128(values.join(''), 31);
        const components = fingerprintResult.reduce((acc, item) => {
            return { ...acc, [item.key]: item.value };
        });

        const resultObj = {
            ...components,
            visitor_fp: visitorFingerprint,
        };

        // if ((ENV !== 'prod' || ALT === 'alt') && IS_BROWSER && window) {
        //     // eslint-disable-next-line no-underscore-dangle
        //     window.__fingerprint = resultObj;
        // }

        memoizedVisitorFingerprint = await encrypt({ visitor_fp: visitorFingerprint });
        memoizedClientFingerprint = await encrypt(resultObj);
        /* END: FPJS 2.0 */
    } catch (err) {
        reportEvent('Error initializing fingerprintJS', 'Fingerprint.initialize()', 'warning', { err });
    }
};

// For regular calls, we only send the visitor fingerprint, not the entire fingerprint
export const getVisitorFingerprint = async (): Promise<string> => {
    if (memoizedVisitorFingerprint) {
        return memoizedVisitorFingerprint;
    }
    await scanFingerprint();
    if (memoizedVisitorFingerprint) {
        return memoizedVisitorFingerprint;
    }
    reportEvent('Could not get visitorID fingerprint', 'getVisitorFingerprint', 'warning');
    return 'sus';
};

// Encrypt -> Encode -> Pack the value+key
const encrypt = async (value: Record<string, unknown>): Promise<string | null> => {
    const PUBLIC_KEY =
        process.env.FP_PUB_KEY ||
        `-----BEGIN PUBLIC KEY-----
MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQC/8LLbavw++/DJJZCS18pFsy6qGmq0e0I1i8mmp2GqgOgVQtgSIvTC3nmm2wyFhm9wJYe4OZy/nb1KV1MSInaMzvqUkTZm0n89QlbXxgBm3CyFPvFEyMN1SZx3oKXQ83Ggsv3ku8nGdlcUHek4mIPPyrqFBRv2d6NT/at9o2RYkwIDAQAB
-----END PUBLIC KEY-----`;

    try {
        const crypto = await import('crypto');
        const jwt = await import('jwt-simple');
        // #TODO: Remove jwt and do https://stackoverflow.com/questions/41043878/symmetric-encryption-with-nodejs

        // 1- Create a random key for JWT
        const jwtKey = crypto.randomBytes(64).toString('base64');

        // 2- Encrypt the data with the symmetric key
        let encodedData = jwt.encode(value, jwtKey);
        encodedData = encodedData
            .split('')
            .reverse()
            .join('');
        const obfuscatedData = `${encodedData}sus`;

        // 3- Encrypt the key with the asymmetric key
        const jwtKeyBuffer = Buffer.from(jwtKey, 'base64');
        const encryptedKey = crypto.publicEncrypt(PUBLIC_KEY, jwtKeyBuffer);
        const encryptedKeyString = encryptedKey.toString('base64');

        // 4- Combine the data
        return `${encryptedKeyString}|${obfuscatedData}`;
    } catch (err) {
        reportEvent('Fingerprint encryption failed', 'Fingerprint.encrypt()', 'warning', { err });
        return null;
    }
};

// Util for compression
const compressCanvas = (value): string => {
    const [, b64] = value.split('data:image/png;base64,');
    const bin = atob(b64);
    const crc = bin2hex(bin.slice(-20, -12));
    return crc;
};

const bin2hex = s => {
    let i;
    let f = 0;
    const a = [];
    f = s.length;

    for (i = 0; i < f; i += 1) {
        a[i] = s
            .charCodeAt(i)
            .toString(16)
            .replace(/^([\da-f])$/, '0$1');
    }

    return a.join('');
};
